import { httpError } from '../utils/userError';
import { getAuthToken } from '../utils/authToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const getHeaders = () => {
    const token = getAuthToken();
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: `API error: ${response.status}` } }));
        throw httpError(response.status, errorData);
    }
    return response.json();
};

/**
 * Compute start/end date strings from a preset number of days.
 * Returns { startDate, endDate } as YYYY-MM-DD strings.
 * endDate is tomorrow (exclusive upper bound).
 */
export function presetToDateRange(presetDays) {
    const now = new Date();
    const end = new Date(now);
    end.setDate(end.getDate() + 1); // tomorrow (exclusive)
    const start = new Date(now);
    start.setDate(start.getDate() - presetDays + 1);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { startDate: fmt(start), endDate: fmt(end) };
}

/** Build URLSearchParams from a dateParams object */
function _dateParams({ startDate, endDate, billingCycle } = {}) {
    const params = new URLSearchParams();
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (billingCycle) params.set('billing_cycle', 'true');
    return params;
}

// The monitoring API is OPERATOR-ONLY — an email allowlist of the two founder
// accounts, server-side — and serves ACCOUNT-WIDE CREDIT STATISTICS ONLY.
// Deleted with the routes behind them, ⚠️ do not add back (the BE routes are
// gone, so a caller would only get an error):
//   * 2026-10-01 — `getConversations` (conversation titles),
//     `getConversationCalls` (message text + raw llm_input/llm_output),
//     `getDocumentUsage` (filenames);
//   * 2026-10-16 (#470, aggregate-only) — `getUsersUsage` (the per-account
//     listing) and `adjustUserEnergy` (energy grants are now the local BE
//     script `scripts/adjust_energy.py`). `getSummary` lost its per-account
//     `userId` argument at the same time: the summary is always account-wide.
export const usageService = {
    async getSummary(dateParams = {}) {
        const params = _dateParams(dateParams);
        const response = await fetch(`${API_BASE_URL}/api/usage/summary?${params}`, { headers: getHeaders() });
        return handleResponse(response);
    },
};
